import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import isEqual from 'fast-deep-equal';

import type {
  Conversation,
  ConversationMessage,
} from '@stripe-internal/conversations-client';
import {ConversationStatus} from '@stripe-internal/conversations-client';
import {getConversationOverview} from '@stripe-internal/conversations-api-client';

import type {Api} from '../../core/lib/api/api';
import {
  buildCanonicalOverviewState,
  type CanonicalOverviewState,
  type CompletionArgs,
  reconcileOverview,
  recordAssistantCompletion,
  recordCallbackQueued,
  recordChatStarted,
  recordCompletion,
} from './canonicalOverviewState';
import {buildConversationLifecycleEventLogHandler} from './conversationLifecycleLog';
import {
  type ChannelResult,
  ThreadsConversationClient,
} from './ThreadsConversationClient';
import {clearStoredConversation} from './storage';
import type {
  ControllerError,
  ControllerResult,
  ConversationOverview,
  ConversationSelection,
  CreateConversationParams,
  ListConversationFilters,
} from './types';
import type {ConversationsBackend, ConversationsController} from '../app/types';
import {
  type ConversationTransition,
  observeConversationTransitions,
} from './observeConversationTransitions';
import {monitorCallbackCompletion} from './monitorCallbackCompletion';
import {NEW_CONVERSATION_ID} from './types';
import {
  resolveTargetChannel,
  selectResumableConversation,
  shouldConnectMessaging,
} from './conversationSelectors';

const unknownError = (error: unknown): ControllerError => ({
  type: 'error',
  error: error instanceof Error ? error.message : 'Unknown conversation error',
});

function hasActiveSupportOverview(
  overview: ConversationOverview | null,
): boolean {
  return (
    overview?.live_chat?.status === 'active' ||
    overview?.request_a_call?.status === 'queued'
  );
}

/**
 * A conversation managed by the controller: the framework model, its
 * routing client, the controller's subscriptions on both, and the per-model
 * bookkeeping needed to project overviews and count unseen messages.
 */
type ManagedConversation = {
  disposed?: boolean;
  model: Conversation;
  client: ThreadsConversationClient;
  subscriptions: Subscription;
  /** The overview last projected from the model's context. */
  contextOverview?: ConversationOverview;
  observedMessageIds: Set<string>;
};

function disposeManagedConversation(managed: ManagedConversation): void {
  if (managed.disposed) {
    return;
  }
  managed.disposed = true;
  managed.subscriptions.unsubscribe();
  managed.model.disconnect();
  managed.client.dispose?.();
}

/**
 * Email still leaves through the SDK because backend end parity for the email
 * channel is deferred; the notice is suppressed because the email form is the
 * user-facing outcome.
 */
async function transitionToEmail(
  conversation: Conversation,
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted) {
    return;
  }
  conversation.setContextValue('supressChatEndedNotice', true);
  const response = await conversation.leave();
  if (response.code >= 400) {
    throw new Error(response.error ?? 'Unable to end conversation');
  }
}

/**
 * Collection ownership for Threads, independent of either screen's view model.
 * The backend owns loading; managed conversations have independent routing
 * clients.
 */
export class ThreadsConversationsController implements ConversationsController {
  private canonicalOverviewStates = new Map<string, CanonicalOverviewState>();

  private overviewRefreshes = new Map<string, Promise<void>>();

  private managedConversations = new Map<string, ManagedConversation>();

  private overviewChanges = new Subject<ConversationOverview>();

  private loadVersionsById = new Map<string, number>();

  private active = true;

  private disposed = false;

  private _loadingId: string | null = null;

  private selectionVersion = 0;

  private selection$ = new BehaviorSubject<ConversationSelection>({
    id: NEW_CONVERSATION_ID,
    conversation: null,
    loadStatus: 'idle',
    error: null,
  });

  get selection(): ConversationSelection {
    return this.selection$.getValue();
  }

  get selectedId(): string {
    return this.selection.id;
  }

  get selected(): Conversation | null {
    return this.selection.conversation;
  }

  selectionObservable() {
    return this.selection$.asObservable();
  }

  private setSelection(update: Partial<ConversationSelection>) {
    this.selection$.next({...this.selection, ...update});
  }

  private backend: ConversationsBackend;

  private withHistory: boolean;

  private api?: Pick<Api, 'endConversation'>;

  get activeSupportId(): string | null {
    for (const [id] of this.canonicalOverviewStates) {
      if (this.hasActiveSupport(id)) {
        return id;
      }
    }
    return null;
  }

  private get backgroundSupportId(): string | null {
    const id = this.activeSupportId;
    return id !== this.selectedId ? id : null;
  }

  private restoringSupport = new Set<string>();

  private callbackMonitors = new Map<string, () => void>();

  private fetchCallbackStatus?: (
    id: string,
  ) => ReturnType<Api['fetchRequestACallCodes']>;

  private lifecycleVersion = 0;

  private unseenCounts$ = new BehaviorSubject<Record<string, number>>({});

  private selectedConversationVisible = true;

  private receivedMessages = new Subject<{
    conversation: Conversation;
    message: ConversationMessage;
  }>();

  receivedMessagesObservable() {
    return this.receivedMessages.asObservable();
  }

  setSelectedConversationVisible(visible: boolean): void {
    this.selectedConversationVisible = visible;
    if (visible) {
      this.setUnseenCount(this.selectedId, 0);
    }
  }

  get unseenMessageCounts(): Readonly<Record<string, number>> {
    return this.unseenCounts$.getValue();
  }

  unseenMessageCountsObservable() {
    return this.unseenCounts$.asObservable();
  }

  private setUnseenCount(id: string, count: number): void {
    if ((this.unseenMessageCounts[id] ?? 0) === count) {
      return;
    }
    this.unseenCounts$.next({...this.unseenMessageCounts, [id]: count});
  }

  constructor(
    backend: ConversationsBackend,
    {
      withHistory = true,
      api,
      fetchCallbackStatus,
    }: {
      withHistory?: boolean;
      api?: Pick<Api, 'endConversation'>;
      fetchCallbackStatus?: (
        id: string,
      ) => ReturnType<Api['fetchRequestACallCodes']>;
    } = {},
  ) {
    this.backend = backend;
    this.withHistory = withHistory;
    this.api = api;
    this.fetchCallbackStatus = fetchCallbackStatus;
  }

  get state() {
    return this.backend.state;
  }

  get stateStore() {
    return this.backend.stateStore;
  }

  getConversation(id: string): Conversation | null {
    return this.managedConversations.get(id)?.model ?? null;
  }

  getOverview(id: string): ConversationOverview | null {
    return this.canonicalOverviewStates.get(id)?.overview ?? null;
  }

  private updateCanonicalOverviewState(
    overviewState: CanonicalOverviewState,
    refetch = false,
  ): void {
    if (this.disposed) {
      return;
    }
    const previous = this.canonicalOverviewStates.get(overviewState.id);
    if (previous === overviewState) {
      return;
    }
    const managed = this.managedConversations.get(overviewState.id);
    const chatCompleted =
      previous?.overview?.live_chat?.status !== 'ended' &&
      overviewState.overview?.live_chat?.status === 'ended';
    if (this.withHistory && chatCompleted) {
      clearStoredConversation(overviewState.id);
    }
    this.canonicalOverviewStates.set(overviewState.id, overviewState);
    if (overviewState.overview?.live_chat?.status === 'ended') {
      managed?.client.cancelChannelTransition();
    }
    if (managed) {
      managed.client.setPollingEnabled(
        shouldConnectMessaging(
          overviewState.overview,
          managed.client.channel,
        ) && managed.client.channel === 'bot',
      );
    }
    if (
      this.active &&
      overviewState.overview &&
      !isEqual(previous?.overview, overviewState.overview)
    ) {
      this.overviewChanges.next(overviewState.overview);
    }
    // Transport lifecycle observers run before delivery to the model.
    if (managed && this.active) {
      this.syncChannel(managed)
        .then(() => {
          if (
            this.active &&
            !managed.disposed &&
            !shouldConnectMessaging(
              this.getOverview(overviewState.id),
              managed.client.channel,
            ) &&
            managed.client.channelState.state !== 'switching'
          ) {
            return managed.model.disconnect();
          }
        })
        .catch(() => undefined);
    }
    this.syncCallbackMonitoring(overviewState.id);
    // Let synchronous transport/model delivery finish before releasing the
    // background runtime, including its final message and lifecycle observers.
    if (
      previous?.overview &&
      hasActiveSupportOverview(previous.overview) &&
      !this.hasActiveSupport(overviewState.id)
    ) {
      Promise.resolve().then(() => {
        if (this.active && !this.disposed) {
          this.releaseDeselected();
        }
      });
    }
    if (refetch) {
      this.refreshOverview(overviewState.id);
    }
  }

  private refreshOverview(id: string): void {
    if (this.disposed || this.overviewRefreshes.has(id)) {
      return;
    }
    const pending = Promise.resolve()
      .then(async () => {
        if (this.disposed) {
          return;
        }
        const response = await this.backend.list({conversationIds: [id]});
        if (!this.disposed && response.type === 'success') {
          this.ingestOverviews(response.data);
        }
      })
      .catch(() => undefined)
      .finally(() => this.overviewRefreshes.delete(id));
    this.overviewRefreshes.set(id, pending);
  }

  retainConversation(conversation: Conversation): void {
    if (this.disposed) {
      conversation.disconnect();
      conversation.client.dispose?.();
      return;
    }
    const previous = this.managedConversations.get(conversation.id);
    if (previous?.model === conversation) {
      this.syncChannel(previous).catch(() => undefined);
      return;
    }
    if (previous) {
      this.callbackMonitors.get(conversation.id)?.();
      this.callbackMonitors.delete(conversation.id);
      disposeManagedConversation(previous);
    }
    let overviewState =
      this.canonicalOverviewStates.get(conversation.id) ??
      buildCanonicalOverviewState(conversation.id);
    if (conversation.client.syncStorageMode) {
      overviewState = reconcileOverview(
        overviewState,
        getConversationOverview(conversation.loadStoredState()?.data.context),
      );
    }
    overviewState = reconcileOverview(
      overviewState,
      getConversationOverview(conversation.context),
    );
    const managed: ManagedConversation = {
      subscriptions: new Subscription(),
      model: conversation,
      client: conversation.client as ThreadsConversationClient,
      observedMessageIds: new Set(
        Object.values(conversation.messages).map((message) => message.id),
      ),
      contextOverview: getConversationOverview(conversation.context),
    };
    this.managedConversations.set(conversation.id, managed);
    const unchanged =
      this.canonicalOverviewStates.get(conversation.id) === overviewState;
    this.updateCanonicalOverviewState(overviewState);
    if (unchanged && this.active && overviewState.overview) {
      this.overviewChanges.next(overviewState.overview);
    }
    this.observeModel(conversation.id, managed);
    this.syncCallbackMonitoring(conversation.id);
    this.syncChannel(managed).catch(() => undefined);
  }

  ingestOverviews = (
    incoming: ConversationOverview[],
  ): ConversationOverview[] =>
    incoming.map((overview) => {
      const overviewState = reconcileOverview(
        this.canonicalOverviewStates.get(overview.id) ??
          buildCanonicalOverviewState(overview.id),
        overview,
      );
      this.updateCanonicalOverviewState(overviewState);
      return overviewState.overview ?? overview;
    });

  recordSupportCompletion(
    conversation: Conversation,
    ...args: CompletionArgs
  ): void {
    const overviewState = this.canonicalOverviewStates.get(conversation.id);
    if (overviewState) {
      this.updateCanonicalOverviewState(
        recordCompletion(overviewState, ...args),
        true,
      );
    }
  }

  overviewChangesObservable() {
    return this.overviewChanges.asObservable();
  }

  activate(): void {
    if (this.active || this.disposed) {
      return;
    }
    this.active = true;
    this.managedConversations.forEach((managed, id) => {
      managed.subscriptions = new Subscription();
      this.observeModel(id, managed);
      this.syncCallbackMonitoring(id);
      this.syncChannel(managed)
        .then((result) => {
          if (
            !this.active ||
            managed.disposed ||
            result === 'failed' ||
            result === 'aborted'
          ) {
            return;
          }
          if (
            (id === this.selectedId || id === this.backgroundSupportId) &&
            shouldConnectMessaging(this.getOverview(id), managed.client.channel)
          ) {
            return managed.model.connect({fetch: true});
          }
        })
        .catch(() => undefined);
    });
  }

  deactivate(): void {
    if (!this.active) {
      return;
    }
    this.active = false;
    this.callbackMonitors.forEach((stop) => stop());
    this.callbackMonitors.clear();
    this.lifecycleVersion += 1;
    this.selectionVersion += 1;
    this._loadingId = null;
    this.loadVersionsById.forEach((version, id) =>
      this.loadVersionsById.set(id, version + 1),
    );
    this.managedConversations.forEach((managed) => {
      managed.client.cancelChannelTransition();
      managed.subscriptions.unsubscribe();
      managed.model.disconnect();
    });
    if (this.selection.loadStatus === 'loading') {
      this.setSelection({loadStatus: 'idle'});
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.deactivate();
    this.disposed = true;
    this.managedConversations.forEach(disposeManagedConversation);
    this.managedConversations.clear();
    this.canonicalOverviewStates.clear();
    this.overviewRefreshes.clear();
    this.loadVersionsById.clear();
    this.setSelection({
      id: NEW_CONVERSATION_ID,
      conversation: null,
      loadStatus: 'idle',
      error: null,
    });
    this.unseenCounts$.next({});
    this.selection$.complete();
    this.overviewChanges.complete();
    this.unseenCounts$.complete();
    this.receivedMessages.complete();
  }

  private hasActiveSupport(id: string): boolean {
    return hasActiveSupportOverview(this.getOverview(id));
  }

  private shouldKeepInBackground(id: string): boolean {
    return (
      this.hasActiveSupport(id) ||
      this.managedConversations.get(id)?.client.channelState.state ===
        'switching'
    );
  }

  private clearPreviousUnseenCount(nextId = NEW_CONVERSATION_ID): void {
    if (!this.withHistory && this.selectedId !== nextId) {
      this.setUnseenCount(this.selectedId, 0);
    }
  }

  private disconnectSuperseded(conversation: Conversation): void {
    if (
      conversation !== this.selected &&
      conversation.id !== this.backgroundSupportId
    ) {
      this.releaseDeselected();
    }
  }

  private observeModel(id: string, managed: ManagedConversation): void {
    if (!this.active) {
      return;
    }
    const isCurrent = () => this.managedConversations.get(id) === managed;
    const abort = new AbortController();
    managed.subscriptions.add(() => abort.abort());
    const observe = observeConversationTransitions({
      initialContext: managed.model.context,
      emailEnabled:
        managed.client.featureFlags.universalHelpUseInlineRacEmailForm,
      transition: (kind) => {
        this.transition(managed, kind, abort.signal).catch((error) => {
          if (!abort.signal.aborted && this.selectedId === id) {
            this.setSelection({error: unknownError(error)});
          }
        });
      },
    });
    managed.subscriptions.add(
      managed.model.contextObservable().subscribe(observe),
    );
    managed.subscriptions.add(
      managed.client.channelStateObservable().subscribe((channelState) => {
        if (channelState.state === 'failed' && this.selectedId === id) {
          this.setSelection({error: unknownError(channelState.error)});
        }
      }),
    );

    const logLifecycleEvent = buildConversationLifecycleEventLogHandler(
      managed.model,
      () => managed.client.featureFlags,
    );
    managed.subscriptions.add(
      managed.client.lifecycleObservable().subscribe((event) => {
        if (!isCurrent()) {
          return;
        }
        if (
          event.name === 'chat-ended' &&
          !managed.model.context.disableCleanupOnEnd
        ) {
          managed.model.disconnect();
        }
        logLifecycleEvent(event);
      }),
    );

    const ingest = (context: Conversation['context']) => {
      if (!isCurrent()) {
        return;
      }
      const incoming = getConversationOverview(context);
      let overviewState = this.canonicalOverviewStates.get(id);
      if (!overviewState) {
        return;
      }
      if (!isEqual(incoming, managed.contextOverview)) {
        managed.contextOverview = incoming;
        overviewState = reconcileOverview(overviewState, incoming);
      }
      const snapshotState = overviewState;
      if (context.channel === 'email') {
        overviewState = recordAssistantCompletion(overviewState);
      }
      if (context.channel === 'request_a_call') {
        overviewState = recordCallbackQueued(overviewState);
      }
      if (
        context.channel === 'chat' &&
        typeof context.chat_conversationId === 'string'
      ) {
        overviewState = recordChatStarted(
          overviewState,
          context.chat_conversationId,
        );
      }
      this.updateCanonicalOverviewState(
        overviewState,
        overviewState !== snapshotState,
      );
      managed.client.setPollingEnabled(
        shouldConnectMessaging(
          overviewState.overview,
          managed.client.channel,
        ) && managed.client.channel === 'bot',
      );
      this.syncChannel(managed).catch(() => undefined);
    };
    ingest(managed.model.context);
    managed.subscriptions.add(
      managed.model.contextObservable().subscribe(ingest),
    );

    managed.subscriptions.add(
      managed.client.chatTransportEventsObservable().subscribe((event) => {
        if (!this.active || !isCurrent()) {
          return;
        }
        const overviewState = this.canonicalOverviewStates.get(id);
        if (
          overviewState &&
          event.name === 'conversation-updated' &&
          event.status === ConversationStatus.Inactive &&
          event.conversationId ===
            (overviewState.overview?.live_chat?.external_id ??
              managed.client.chatConversationId)
        ) {
          this.updateCanonicalOverviewState(
            recordCompletion(overviewState, 'live_chat', event.conversationId),
            true,
          );
        }
      }),
    );

    managed.observedMessageIds = new Set(
      Object.values(managed.model.messages).map((message) => message.id),
    );
    managed.subscriptions.add(
      managed.model.messagesObservable().subscribe((incoming) => {
        let count = 0;
        Object.values(incoming).forEach((message) => {
          if (
            !managed.observedMessageIds.has(message.id) &&
            message.participant.id !== managed.model.user.id
          ) {
            count += 1;
            this.receivedMessages.next({conversation: managed.model, message});
          }
          managed.observedMessageIds.add(message.id);
        });
        const countsAsUnseen =
          id === this.selectedId
            ? !this.selectedConversationVisible
            : this.withHistory &&
              !!(
                this.getOverview(id)?.live_chat ||
                this.getOverview(id)?.request_a_call
              );
        if (count && countsAsUnseen) {
          this.setUnseenCount(id, (this.unseenMessageCounts[id] ?? 0) + count);
        }
      }),
    );
  }

  private async transition(
    managed: ManagedConversation,
    kind: ConversationTransition,
    signal: AbortSignal,
  ): Promise<void> {
    if (kind === 'rac') {
      await managed.model.disconnect();
    } else if (kind === 'restart') {
      await this.restartServerConversation(managed, signal);
    } else {
      if (
        !signal.aborted &&
        this.managedConversations.get(managed.model.id) === managed
      ) {
        this.updateCanonicalOverviewState(
          recordAssistantCompletion(
            this.canonicalOverviewStates.get(managed.model.id) ??
              buildCanonicalOverviewState(managed.model.id),
          ),
          true,
        );
      }
      await transitionToEmail(managed.model, signal);
    }
    if (!signal.aborted) {
      this.disconnectSuperseded(managed.model);
    }
  }

  private async restartServerConversation(
    managed: ManagedConversation,
    signal: AbortSignal,
  ): Promise<void> {
    const {model} = managed;
    if (managed.client.channel !== 'bot') {
      throw new Error('Can only restart bot conversations');
    }
    await model.disconnect();
    if (signal.aborted) {
      return;
    }
    if (!this.api) {
      throw new Error('Conversation API unavailable');
    }
    const ended = await this.api.endConversation(model.id);
    if (signal.aborted) {
      return;
    }
    if (ended.type === 'error') {
      throw new Error(ended.error);
    }
    clearStoredConversation(model.id);
    this.updateCanonicalOverviewState(
      recordAssistantCompletion(
        this.canonicalOverviewStates.get(model.id) ??
          buildCanonicalOverviewState(model.id),
      ),
      true,
    );
    const context: Conversation['context'] = {
      ...model.context,
      intent: undefined,
      channel: 'bot',
    };
    delete context.bot_conversationId;
    delete context.chat_conversationId;
    delete context.conversationOverview;
    const result = await this.create({context});
    if (signal.aborted) {
      return;
    }
    if (result.type === 'error') {
      throw new Error(result.error);
    }
    if (this.selectedId === model.id) {
      this.selectModel(result.data, {
        id: result.data.id,
        loadStatus: 'success',
        error: null,
      });
      await result.data.connect({fetch: false});
    } else {
      this.disconnectSuperseded(result.data);
    }
  }

  private syncChannel(
    managed: ManagedConversation,
  ): Promise<ChannelResult | null> {
    if (!this.active || managed.disposed) {
      return Promise.resolve(null);
    }
    const target = resolveTargetChannel(
      this.canonicalOverviewStates.get(managed.model.id),
      managed.model.context,
    );
    if (!target) {
      return Promise.resolve(null);
    }
    return managed.client.ensureChannel(target);
  }

  private channelError(
    managed: ManagedConversation,
    result: ChannelResult | null,
  ): Error | null {
    if (result === 'failed') {
      return (
        managed.client.channelState.error ??
        new Error('Conversation channel binding failed')
      );
    }
    if (
      result === 'aborted' &&
      (this.getOverview(managed.model.id)?.live_chat?.status === 'active' ||
        shouldConnectMessaging(
          this.getOverview(managed.model.id),
          managed.client.channel,
        ))
    ) {
      return new Error('Conversation channel binding interrupted');
    }
    return null;
  }

  resumeOrCreate = async (
    resolveActive: () => Promise<ConversationOverview | undefined>,
    createParams?: CreateConversationParams,
  ): Promise<void> => {
    if (this.disposed) {
      return;
    }
    this.selectionVersion += 1;
    const {selectionVersion} = this;
    this._loadingId = null;
    this.setSelection({loadStatus: 'loading', error: null});
    let active: ConversationOverview | undefined;
    try {
      active = await resolveActive();
    } catch (error) {
      if (selectionVersion !== this.selectionVersion) {
        return;
      }
      this.setSelection({
        loadStatus: 'error',
        error: unknownError(error),
      });
      return;
    }
    if (selectionVersion !== this.selectionVersion) {
      return;
    }
    if (active) {
      this.ingestOverviews([active]);
      await this.select(active.id);
    } else {
      await this.startNew(createParams);
    }
  };

  select = async (id: string): Promise<void> => {
    if (this.disposed) {
      return;
    }
    const managed = this.managedConversations.get(id);
    const reuse =
      managed &&
      (id === this.selectedId ||
        (this.withHistory && this.shouldKeepInBackground(id)));
    if (this._loadingId === id && id !== NEW_CONVERSATION_ID && !reuse) {
      return;
    }
    this.selectionVersion += 1;
    const {selectionVersion} = this;
    if (id === NEW_CONVERSATION_ID) {
      this._loadingId = null;
      this.clearPreviousUnseenCount();
      this.selectModel(null, {
        id,
        loadStatus: 'idle',
        error: null,
      });
      return;
    }

    const cachedConversation = reuse ? managed.model : null;
    if (this.selectedId !== id) {
      this.clearPreviousUnseenCount(id);
    }
    this._loadingId = id;
    this.selectModel(cachedConversation, {
      id,
      loadStatus: 'loading',
      error: null,
    });
    try {
      if (reuse) {
        this.beginRequest(id);
        const result = await this.syncChannel(managed);
        if (selectionVersion !== this.selectionVersion) {
          return;
        }
        const error = this.channelError(managed, result);
        if (error) {
          this.setSelection({
            loadStatus: 'error',
            error: unknownError(error),
          });
          this._loadingId = null;
          return;
        }
        if (
          shouldConnectMessaging(this.getOverview(id), managed.client.channel)
        ) {
          await managed.model.connect({fetch: true});
        } else {
          await managed.model.disconnect();
        }
        if (selectionVersion !== this.selectionVersion) {
          return;
        }
        this.setSelection({loadStatus: 'success', error: null});
        this._loadingId = null;
        return;
      }
      const response = await this.load(id);
      if (selectionVersion !== this.selectionVersion) {
        if (response.type === 'success') {
          this.disconnectSuperseded(response.data);
        }
        return;
      }
      if (response.type === 'error') {
        this.setSelection({
          error: response,
          loadStatus: 'error',
        });
        this._loadingId = null;
        return;
      }
      const conversation = response.data;
      const loaded = this.managedConversations.get(conversation.id);
      const channelResult = loaded ? await this.syncChannel(loaded) : null;
      if (selectionVersion !== this.selectionVersion) {
        return;
      }
      const channelError = loaded
        ? this.channelError(loaded, channelResult)
        : null;
      if (channelError) {
        this.selectModel(conversation, {
          id: conversation.id,
          loadStatus: 'error',
          error: unknownError(channelError),
        });
        this._loadingId = null;
        return;
      }
      const overview = this.getOverview(conversation.id);
      if (shouldConnectMessaging(overview, conversation.client.channel)) {
        conversation.connect({fetch: false});
      } else {
        conversation.disconnect();
      }
      if (selectionVersion !== this.selectionVersion) {
        this.disconnectSuperseded(conversation);
        return;
      }
      this.selectModel(conversation, {
        id: conversation.id,
        loadStatus: 'success',
        error: null,
      });
      this._loadingId = null;
    } catch (error) {
      if (selectionVersion !== this.selectionVersion) {
        return;
      }
      this.setSelection({
        ...(cachedConversation ? {} : {error: unknownError(error)}),
        loadStatus: 'error',
      });
      this._loadingId = null;
    }
  };

  startNew = async (
    params?: CreateConversationParams,
  ): ControllerResult<Conversation> => {
    if (this.disposed) {
      return {type: 'error', error: 'Conversation controller disposed'};
    }
    const id = NEW_CONVERSATION_ID;
    if (this._loadingId === id) {
      return {type: 'error', error: 'Conversation creation already pending'};
    }
    this.selectionVersion += 1;
    const {selectionVersion} = this;
    this.clearPreviousUnseenCount();
    this._loadingId = id;
    this.selectModel(null, {id, loadStatus: 'loading', error: null});
    try {
      const response = await this.createAndLoad({...params});
      if (selectionVersion !== this.selectionVersion) {
        if (response.type === 'success') {
          this.disconnectSuperseded(response.data);
        }
        return response;
      }
      if (response.type === 'error') {
        this.setSelection({
          loadStatus: 'error',
          error: response,
        });
        this._loadingId = null;
        return response;
      }
      const conversation = response.data;
      const managed = this.managedConversations.get(conversation.id);
      const channelResult = managed ? await this.syncChannel(managed) : null;
      if (selectionVersion !== this.selectionVersion) {
        return response;
      }
      const channelError = managed
        ? this.channelError(managed, channelResult)
        : null;
      if (channelError) {
        throw channelError;
      }
      if (
        shouldConnectMessaging(
          this.getOverview(conversation.id),
          conversation.client.channel,
        )
      ) {
        conversation.connect({fetch: false});
      }
      this.selectModel(conversation, {
        id: conversation.id,
        loadStatus: 'success',
        error: null,
      });
      this._loadingId = null;
      return response;
    } catch (error) {
      const controllerError = unknownError(error);
      if (selectionVersion === this.selectionVersion) {
        this.setSelection({
          loadStatus: 'error',
          error: controllerError,
        });
        this._loadingId = null;
      }
      return controllerError;
    }
  };

  private selectModel(
    conversation: Conversation | null,
    state: Partial<ConversationSelection>,
  ) {
    this.setSelection({...state, conversation});
    if (this.selectedConversationVisible) {
      this.setUnseenCount(this.selectedId, 0);
    }
    this.releaseDeselected();
    this.restoreBackgroundSupport();
  }

  /**
   * Releases every managed conversation except the selected one, the one
   * loading for the selection, and the background support slot.
   */
  private releaseDeselected(): void {
    for (const [id, managed] of this.managedConversations) {
      if (
        id !== this.selectedId &&
        id !== this._loadingId &&
        !(this.withHistory && this.shouldKeepInBackground(id))
      ) {
        this.callbackMonitors.get(id)?.();
        this.callbackMonitors.delete(id);
        disposeManagedConversation(managed);
        this.managedConversations.delete(id);
      }
    }
  }

  list = async (
    filters?: ListConversationFilters,
  ): ControllerResult<ConversationOverview[]> => {
    if (this.disposed) {
      return {type: 'error', error: 'Conversation controller disposed'};
    }
    try {
      const response = await this.backend.list(filters);
      if (this.disposed) {
        return {type: 'error', error: 'Conversation controller disposed'};
      }
      if (response.type === 'error') {
        return response;
      }
      const data = this.ingestOverviews(response.data);
      this.restoreBackgroundSupport();
      return {...response, data};
    } catch (error) {
      return {
        type: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'Unable to list conversations',
      };
    }
  };

  findResumableConversation = async (): ControllerResult<
    ConversationOverview | undefined
  > => {
    const response = await this.list({limit: 10, status: 'active'});
    if (response.type === 'error') {
      return response;
    }
    return {
      type: 'success',
      data: selectResumableConversation(response.data),
    };
  };

  load = async (id: string): ControllerResult<Conversation> => {
    if (this.disposed) {
      return {type: 'error', error: 'Conversation controller disposed'};
    }
    const request = this.beginRequest(id);
    const response = await this.backend.load(id);
    if (!this.active || this.loadVersionsById.get(id) !== request) {
      if (
        response.type === 'success' &&
        response.data &&
        this.getConversation(id) !== response.data
      ) {
        response.data.disconnect();
        response.data.client.dispose?.();
      }
      return {type: 'error', error: 'Conversation request superseded'};
    }
    if (response.type === 'success') {
      this.retainConversation(response.data);
    }
    return response;
  };

  create = (
    params?: CreateConversationParams,
  ): ControllerResult<Conversation> =>
    this.retainCreatedConversation(() => this.backend.create(params));

  createAndLoad = (
    params?: CreateConversationParams,
  ): ControllerResult<Conversation> =>
    this.retainCreatedConversation(() => this.backend.createAndLoad(params));

  private async retainCreatedConversation(
    create: () => ControllerResult<Conversation>,
  ): ControllerResult<Conversation> {
    if (this.disposed) {
      return {type: 'error', error: 'Conversation controller disposed'};
    }
    const lifecycle = this.lifecycleVersion;
    const response = await create();
    if (lifecycle !== this.lifecycleVersion || !this.active) {
      if (response.type === 'success') {
        response.data.disconnect();
        response.data.client.dispose?.();
      }
      return {type: 'error', error: 'Conversation request superseded'};
    }
    if (response.type === 'success') {
      this.retainConversation(response.data);
    }
    return response;
  }

  end: ConversationsBackend['end'] = async (id) => {
    if (this.disposed) {
      return {type: 'error', error: 'Conversation controller disposed'};
    }
    const managed = this.managedConversations.get(id);
    const response = await this.backend.end(id);
    if (
      response.type === 'success' &&
      !this.disposed &&
      (!managed || this.managedConversations.get(id) === managed)
    ) {
      let overviewState =
        this.canonicalOverviewStates.get(id) ?? buildCanonicalOverviewState(id);
      const chat = overviewState.overview?.live_chat;
      if (chat) {
        overviewState = recordCompletion(
          overviewState,
          'live_chat',
          chat.external_id,
        );
      } else if (overviewState.overview?.request_a_call) {
        overviewState = recordCompletion(overviewState, 'request_a_call');
      } else {
        overviewState = recordAssistantCompletion(overviewState);
      }
      this.updateCanonicalOverviewState(overviewState, true);
      if (managed) {
        managed.model.disconnect();
      }
    }
    return response;
  };

  private restoreBackgroundSupport(): void {
    const id = this.backgroundSupportId;
    if (
      !this.active ||
      !this.withHistory ||
      !this.selected ||
      !id ||
      this.managedConversations.has(id) ||
      this.restoringSupport.has(id)
    ) {
      return;
    }
    this.restoringSupport.add(id);
    this.load(id)
      .then(async (response) => {
        if (response.type !== 'success') {
          return;
        }
        const managed = this.managedConversations.get(id);
        if (!managed || managed.model !== response.data || !this.active) {
          return;
        }
        if (!this.hasActiveSupport(id)) {
          this.releaseDeselected();
          return;
        }
        const result = await this.syncChannel(managed);
        if (
          this.active &&
          !managed.disposed &&
          !this.channelError(managed, result) &&
          shouldConnectMessaging(this.getOverview(id), managed.client.channel)
        ) {
          await managed.model.connect({fetch: true});
        }
      })
      .catch(() => undefined)
      .finally(() => this.restoringSupport.delete(id));
  }

  private syncCallbackMonitoring(id: string): void {
    const managed = this.managedConversations.get(id);
    if (
      !this.active ||
      !managed ||
      managed.disposed ||
      this.getOverview(id)?.request_a_call?.status !== 'queued'
    ) {
      this.callbackMonitors.get(id)?.();
      this.callbackMonitors.delete(id);
      return;
    }
    const fetchStatus = this.fetchCallbackStatus;
    if (!fetchStatus || this.callbackMonitors.has(id)) {
      return;
    }
    this.callbackMonitors.set(
      id,
      monitorCallbackCompletion({
        fetchStatus: () => fetchStatus(id),
        onComplete: () => {
          if (this.active && this.managedConversations.get(id) === managed) {
            this.recordSupportCompletion(managed.model, 'request_a_call');
          }
        },
      }),
    );
  }

  private beginRequest(id: string): number {
    const request = (this.loadVersionsById.get(id) ?? 0) + 1;
    this.loadVersionsById.set(id, request);
    return request;
  }
}
